import { useEffect, useState } from 'react';
import { trackStoreEvent } from '../../lib/dtpAnalytics';
import { Link, useNavigate } from 'react-router-dom';
import type { CustomStoreDesign, CustomStoreProduct } from '@bloom/shared';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { dtpApi } from '../../lib/dtpApi';
import { doseFamilyKey, useDtpCartStore } from '../../store/useDtpCartStore';
import { CustomProductPageView } from '../../components/store/custom/CustomProductPageView';
import { useTrackWhen } from '../../components/analytics/useTrackWhen';

export function DtpProductPage() {
  const { physicianId, slug } = useDtpParams<{ physicianId: string; slug: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<{ design: CustomStoreDesign; products: CustomStoreProduct[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { addItem, applyClinicFromCatalog } = useDtpCartStore();
  useEffect(() => {
    let active = true;
    setLoading(true); setError(false); setData(null);
    if (!physicianId) { setLoading(false); return; }
    dtpApi.getCatalog(physicianId).then((res: any) => {
      if (!active) return;
      if (!res.success) throw new Error('Catalog unavailable');
      applyClinicFromCatalog(res.data.clinic);
      if (res.data.clinic.storefrontVariant === 'custom' && res.data.clinic.customDesign) {
        setData({ design: res.data.clinic.customDesign, products: res.data.products });
      }
    }).catch(() => { if (active) setError(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [physicianId, applyClinicFromCatalog]);
  const page = data?.design.productPages?.find(p => p.slug === slug);
  const product = data?.products.find(p => p._id === page?.productId);
  useTrackWhen(!!product, 'product_viewed', { productId: product?._id });
  if (loading) return <p role="status" className="py-12">Loading treatment…</p>;
  return <div className="max-w-5xl mx-auto px-4 py-8">
    <Link className="underline" to={dtpPath(physicianId)}>Back to treatments</Link>
    {error ? <p role="alert" className="py-12">This treatment could not be loaded. Please refresh to retry.</p>
      : !page || !product || !data ? <h1 className="py-12 text-2xl">This treatment is unavailable</h1>
      : <CustomProductPageView design={data.design} page={page} product={product} onCheckout={() => {
        addItem({ productId: product._id, productName: product.displayName, price: product.price, family: doseFamilyKey(product) });
        trackStoreEvent('add_to_cart', { productId: product._id, source: 'product-page' });
        navigate(dtpPath(physicianId, '/checkout'));
      }} />}
  </div>;
}
